import { useCallback, useEffect, useState } from 'react';
import { useMemo } from 'react';

import {
  MessageFieldsFragment,
  useCreateMessageMutation,
  useListMessagesQuery,
} from '@/@generated/codegen/API';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import getLastReadChatMessageKey from '@/utils/helpers/getLastReadChatMessageKey';
import { captureSentry } from '@/utils/helpers/sentryHelper';

function useChatMessages(channelID: string | undefined) {
  const [messages, setMessages] = useState<MessageFieldsFragment[]>([]);
  const [lastReadMessageID, setLastReadMessageID] = useState<string | null>(null);
  const [mutationCreateMessage] = useCreateMessageMutation();
  const messagesQuery = useListMessagesQuery({
    variables: {
      orderByArgs: {
        orderByField: 'createdAt',
        direction: 'desc',
      },
      // NOTE: It would be nice to also handle pagination when scrolling to see old messages but
      // it's not needed right now.
      paginationArgs: {
        first: 100,
      },
      where: {
        meetingId: {
          equals: channelID,
        },
      },
    },
    skip: !channelID,
  });

  const addMessage = useCallback(
    async ({ channelId, body }: { channelId: string; body: string }) => {
      try {
        await mutationCreateMessage({
          variables: {
            data: {
              body,
              meetingId: channelId,
            },
          },
        });
      } catch (err) {
        captureSentry({
          title: 'useMessageActions | addMessage error',
          detail: {
            channelId,
            body,
            error: err,
          },
        });
      }
    },
    [mutationCreateMessage]
  );

  useEffect(() => {
    if (!messagesQuery.data) {
      setMessages([]);
      return;
    }
    setMessages(messagesQuery.data.listMessages.edges.map((edge) => edge.node));
  }, [messagesQuery.data]);

  const amtUnreadMessages = useMemo(() => {
    if (!lastReadMessageID) return messages.length;
    const lastReadMessage = messages.find((elem) => elem.id === lastReadMessageID);
    if (!lastReadMessage) {
      if (messagesQuery.loading) return 0;
      captureSentry({
        title: 'useChatMessages | lastReadMessage not found in messages array',
        detail: {
          lastReadMessageID,
          messages,
          channelID: channelID,
        },
      });
      return 0;
    }
    const lastReadMessageIndex = messages.indexOf(lastReadMessage);

    return messages.length - lastReadMessageIndex - 1;
  }, [messages, lastReadMessageID]);

  //NOTE: load from localstorage the last read message for this channel
  useEffect(() => {
    setLastReadMessageID(localStorage.getItem(getLastReadChatMessageKey(channelID)));
  }, [channelID]);

  useEntitySubscription('Message', {
    onCreate: {
      variables: {
        where: {
          meetingId: {
            equals: channelID,
          },
        },
      },
      skip: !channelID,
    },
    onDelete: {
      variables: {},
      skip: !channelID,
    },
  });

  return {
    isLoading: messagesQuery.loading,
    messages: messages,
    unreadMessages: amtUnreadMessages,
    markAllMessagesAsRead: () => {
      if (messages.length === 0) return;
      const messageID = messages[messages.length - 1].id;
      setLastReadMessageID(messageID);
      localStorage.setItem(getLastReadChatMessageKey(channelID || ''), messageID);
    },
    addMessage,
  };
}

export default useChatMessages;
